import { z } from 'zod'

import { SuperSource } from './super'

const GeneratedWorkflowStepSchema = z.object({
  explanation: z
    .string()
    .describe(
      "What this step should accomplish. The first line is a short, self-explanatory action name (50 chars max). Use generic references instead of specific values that might change per query. The user's question may override time frames, counts, or other parameters at runtime. Most steps need only the first line — only add a second line (after \\n) when it provides genuinely new direction that the action name alone doesn't convey. Never repeat or paraphrase the first line. The second line should add specifics like what fields to focus on, what to filter by, or what angle to take — not restate the task."
    ),
  sourceTypes: z
    .array(z.enum(SuperSource))
    .optional()
    .describe(
      'Optional: which data sources this step should use. Only set when the step clearly involves specific sources.'
    ),
  parentIds: z
    .array(z.string())
    .optional()
    .describe(
      'Optional: narrow within sources to specific nodes (teams, repos, channels). Use searchParents to find valid IDs first.'
    ),
  parentDetails: z
    .array(
      z.object({
        id: z.string(),
        title: z.string(),
        source: z.enum(SuperSource),
        iconShape: z.string().nullish(),
        iconColor: z.string().nullish(),
      })
    )
    .optional()
    .describe(
      'Display metadata for parent nodes. Include when parentIds are set — use the data returned by searchParents.'
    ),
  iconId: z
    .string()
    .optional()
    .describe(
      'Step icon override. Auto-derived from sourceTypes when a single source is set, so only needed for: (1) multi-source steps — pick the primary SuperSource name (e.g. "Linear", "GitHub", "Slack Channel") or "semanticSearch"; (2) non-source steps — use a utility icon: "semanticSearch", "webSearch", "chartTool", "read", "file", "code". Omit for single-source steps (auto-derived).'
    ),
})

/** The assistant configuration the workflow builder agent hands back. */
export const GeneratedWorkflowSchema = z.object({
  title: z
    .string()
    .describe(
      'Short, descriptive assistant name (50 chars max). Pattern: [Action/Output] + [Object/Context]. E.g. "Changelog generator", "Account summarizer".'
    ),
  description: z
    .string()
    .describe(
      'Brief, user-friendly description of what this assistant does. 80 chars max.'
    ),
  iconShape: z
    .string()
    .optional()
    .describe(
      'Emoji short name for the assistant icon (NOT the raw emoji character). Common choices: bulb, rocket, memo, sparkles, dart, briefcase, chart, bar_chart, books, gear, fire, zap, hammer_and_wrench, speech_balloon, crystal_ball, test_tube, pencil2, scroll, eyes, warning, muscle, moneybag, handshake, earth_americas, calendar, white_check_mark, robot_face, mag, key, book, clipboard, pushpin, trophy, heart, seedling.'
    ),
  iconColor: z
    .string()
    .optional()
    .describe(
      'Icon color ID. Valid values: dark, grey, yellow, orange, pink, purple, blue, turquoise, green.'
    ),
  customPrompt: z
    .string()
    .optional()
    .describe(
      'ONLY for genuinely unique behavioral constraints or domain-specific context. Omit for most workflows. Do NOT fill with generic advice.'
    ),
  outputGuidance: z
    .string()
    .optional()
    .describe(
      'How to analyze and present the final output. Include synthesis instructions here (grouping, prioritization, themes) — NOT as steps. E.g. "Group bugs by priority, highlight common root causes, present as a table with impact and status columns".'
    ),
  welcomeMessage: z
    .string()
    .optional()
    .describe(
      'Friendly welcome message shown when users open this assistant. Explain what it does and prompt for what you need. Keep casual and concise (2-3 sentences).'
    ),
  kickstartButtons: z
    .array(z.string())
    .optional()
    .describe(
      'Quick-start buttons shown below the welcome message. Each must contain ALL inputs needed to run immediately (e.g. "Last 7 days", "This month"). Only include if realistic example inputs are possible.'
    ),
  steps: z
    .array(GeneratedWorkflowStepSchema)
    .describe(
      'Action steps only — concrete data-fetching tasks (search, query, look up). 1-3 steps typical. Do NOT include synthesis, analysis, or formatting steps — those belong in outputGuidance.'
    ),
})

export type GeneratedWorkflow = z.output<typeof GeneratedWorkflowSchema>
